/**
 * Генератор расписания по системе "круговой турнир"
 * Алгоритм: метод ротации с шагом N/2 и чередованием дом/гости
 * 
 * Для N команд: N-1 туров по N/2 матчей в каждом туре (1 круг)
 * Если N нечётное, добавляется фиктивная команда (команда отдыхает)
 * 
 * Последняя команда (N) — фиксированная, остальные (1..N-1) располагаются
 * в кольце и ротируются на N/2 позиций каждый тур.
 * 
 * Нечётные туры: верхняя половина слотов (N/2..N-2) — дома,
 *   нижняя (0..N/2-1) — в гостях, фиксированная — дома.
 * Чётные туры: слот 0 — дома, нижние слоты (1..N/2-1) — в гостях,
 *   верхние (N/2..N-2) — дома, фиксированная — в гостях.
 * 
 * Максимум 2 матча подряд дома или в гостях для каждой команды.
 */

import moment from 'moment';

/**
 * Генерирует расписание матчей для кругового турнира
 * @param {Array} teams - массив команд [{team_id, team_name, logo, ...}]
 * @param {Object} options - параметры генерации
 * @param {string} options.firstRoundDate - дата первого тура (YYYY-MM-DD)
 * @param {number} options.rounds - количество кругов (1 или 2)
 * @param {number} options.halfTimeDuration - время тайма в минутах (по умолчанию 30)
 * @param {number} options.stadiumId - ID стадиона (по умолчанию 14 - не назначен)
 * @param {string} options.stadiumName - название стадиона
 * @param {number} options.refereeId - ID судьи (может быть null)
 * @param {string} options.refereeName - имя судьи
 * @param {number} options.daysBetweenRounds - дней между турами (по умолчанию 7)
 * @param {boolean} options.withDates - ставить даты турам (false — календарь только по турам, date: null)
 * @returns {Array} массив туров с матчами
 */
export function generateSchedule(teams, options) {
  const {
    firstRoundDate,
    rounds = 1,
    halfTimeDuration = 30,
    stadiumId = 14,
    stadiumName = 'Не назначен',
    refereeId = null,
    refereeName = '',
    daysBetweenRounds = 7,
    withDates = true,
  } = options;

  if (!teams || teams.length < 2) {
    return [];
  }

  const teamsList = [...teams];
  const isOdd = teamsList.length % 2 !== 0;

  // Если нечётное число команд — добавляем "пустую" команду
  if (isOdd) {
    teamsList.push({ team_id: null, team_name: 'Пропуск (выходной)', logo: null });
  }

  const N = teamsList.length;
  const halfN = N / 2;
  const totalRoundsPerCircle = N - 1;

  // Фиксированная команда — последняя (индекс N-1)
  const fixed = teamsList[N - 1];
  // Кольцо — все остальные команды (индексы 0..N-2)
  const initialCircle = teamsList.slice(0, N - 1);

  const allTours = [];

  for (let circleIdx = 0; circleIdx < rounds; circleIdx++) {
    for (let round = 0; round < totalRoundsPerCircle; round++) {
      const tourNumber = circleIdx * totalRoundsPerCircle + round + 1;
      const tourDate = withDates
        ? moment(firstRoundDate)
          .add((tourNumber - 1) * daysBetweenRounds, 'days')
          .format('YYYY-MM-DD')
        : null;

      // Ротация кольца: сдвиг на round * (N/2) позиций
      const offset = (round * halfN) % (N - 1);
      const slots = [];
      for (let i = 0; i < N - 1; i++) {
        slots.push(initialCircle[(i + offset) % (N - 1)]);
      }

      const roundNum = round + 1; // 1-indexed
      const isOddRound = roundNum % 2 === 1;
      const isFirstRound = round === 0 && circleIdx === 0;
      const swap = circleIdx > 0; // второй круг — меняем дом/гости

      const rawMatches = [];

      if (isOddRound) {
        // Нечётный тур: фиксированная команда — дома, слот 0 — в гостях
        // Не фиксированные пары: inner-to-outer
        // (slot[N/2+i], slot[N/2-1-i]) для i=0..N/2-2

        const nonFixed = [];
        for (let i = 0; i < halfN - 1; i++) {
          let home = slots[halfN + i];
          let away = slots[halfN - 1 - i];
          if (swap) { const tmp = home; home = away; away = tmp; }
          nonFixed.push({ home, away });
        }

        let fixedHome = swap ? slots[0] : fixed;
        let fixedAway = swap ? fixed : slots[0];
        const fixedMatch = { home: fixedHome, away: fixedAway };

        // Тур 1: не фиксированные сначала, фиксированный в конце
        // Туры 3, 5, 7...: фиксированный в начале
        if (isFirstRound) {
          rawMatches.push(...nonFixed, fixedMatch);
        } else {
          rawMatches.push(fixedMatch, ...nonFixed);
        }
      } else {
        // Чётный тур: слот 0 — дома, фиксированная — в гостях
        // Не фиксированные пары: outer-to-inner
        // (slot[N-2-i], slot[1+i]) для i=0..N/2-2

        let fixedHome = swap ? fixed : slots[0];
        let fixedAway = swap ? slots[0] : fixed;
        rawMatches.push({ home: fixedHome, away: fixedAway });

        for (let i = 0; i < halfN - 1; i++) {
          let home = slots[N - 2 - i];
          let away = slots[1 + i];
          if (swap) { const tmp = home; home = away; away = tmp; }
          rawMatches.push({ home, away });
        }
      }

      // Фильтруем матчи с фиктивной командой (нечётное число команд)
      const filteredMatches = rawMatches.filter(
        m => m.home.team_id !== null && m.away.team_id !== null
      );

      const matches = filteredMatches.map((m, idx) => ({
        id: `gen_${tourNumber}_${idx}`,
        home_team: m.home,
        away_team: m.away,
        date: tourDate,
        time: null,
        stadium_id: stadiumId,
        stadium_name: stadiumName,
        referee_id: refereeId,
        referee_name: refereeName,
        half_time_duration: halfTimeDuration,
        tour_number: tourNumber,
      }));

      allTours.push({
        tour_number: tourNumber,
        date: tourDate,
        matches,
        circle: circleIdx + 1,
      });
    }
  }

  return allTours;
}

/**
 * Восстанавливает порядок команд (teamsList) из матчей первого тура.
 * 
 * Для чётного числа команд (N матчей = N/2):
 * - Матчи 0..N/2-2: match[i].home = slot[N/2 + i], match[i].away = slot[N/2 - 1 - i]
 * - Последний матч: match[N/2-1].home = fixed (teamsList[N-1]), match[N/2-1].away = slot[0]
 * 
 * Для нечётного числа команд: одна команда не участвует в первом туре (bye).
 * Она занимает позицию slot[0] (играет с фиксированной = bye).
 * 
 * @param {Array} firstRoundMatches - матчи первого тура [{home: team, away: team}, ...]
 * @param {Array} allTeams - все команды лиги (для определения bye-команды при нечётном числе)
 * @returns {Array} упорядоченный массив команд для передачи в generateSchedule
 */
export function rebuildTeamsOrderFromFirstRound(firstRoundMatches, allTeams) {
  const numMatches = firstRoundMatches.length;
  const isOdd = allTeams && allTeams.length % 2 !== 0;
  const N = isOdd ? allTeams.length + 1 : numMatches * 2;
  const halfN = N / 2;

  const teamsList = new Array(N);

  if (isOdd) {
    // Нечётное число команд: dummy = фиксированная (последняя позиция)
    // Все пользовательские матчи — это НЕ фиксированные пары
    // match[i]: home = slot[halfN + i], away = slot[halfN - 1 - i]
    const dummyTeam = { team_id: null, team_name: 'Пропуск (выходной)', logo: null };
    teamsList[N - 1] = dummyTeam; // fixed = dummy

    for (let i = 0; i < numMatches; i++) {
      teamsList[halfN + i] = firstRoundMatches[i].home;
      teamsList[halfN - 1 - i] = firstRoundMatches[i].away;
    }

    // slot[0] = bye-команда (та, что не участвует в первом туре)
    const usedIds = new Set();
    firstRoundMatches.forEach(m => {
      usedIds.add(m.home.team_id || m.home.id);
      usedIds.add(m.away.team_id || m.away.id);
    });
    const byeTeam = allTeams.find(t => !usedIds.has(t.team_id || t.id));
    teamsList[0] = byeTeam;
  } else {
    // Чётное число команд
    const lastMatch = firstRoundMatches[numMatches - 1];
    teamsList[N - 1] = lastMatch.home; // fixed
    teamsList[0] = lastMatch.away;     // slot[0]

    for (let i = 0; i < numMatches - 1; i++) {
      teamsList[halfN + i] = firstRoundMatches[i].home;
      teamsList[halfN - 1 - i] = firstRoundMatches[i].away;
    }
  }

  return teamsList;
}

/**
 * Генерирует расписание на основе заданных матчей первого тура.
 * Восстанавливает порядок команд и запускает стандартный алгоритм.
 * 
 * @param {Array} firstRoundMatches - матчи [{home: teamObj, away: teamObj}, ...]
 * @param {Object} options - те же параметры что и в generateSchedule
 * @param {Array} allTeams - все команды лиги (для определения bye при нечётном числе)
 * @returns {Array} массив туров с матчами
 */
export function generateScheduleFromFirstRound(firstRoundMatches, options, allTeams) {
  const orderedTeams = rebuildTeamsOrderFromFirstRound(firstRoundMatches, allTeams);
  return generateSchedule(orderedTeams, options);
}
/**
 * Проверяет уникальность матчей в рамках каждого круга
 */
export function validateSchedule(tours) {
  const circles = {};
  const duplicates = [];

  for (const tour of tours) {
    const circle = tour.circle || 1;
    if (!circles[circle]) circles[circle] = new Set();

    for (const match of tour.matches) {
      const pair = [match.home_team.team_id, match.away_team.team_id].sort().join('-');
      if (circles[circle].has(pair)) {
        duplicates.push({
          tour: tour.tour_number,
          circle,
          home: match.home_team.team_name,
          away: match.away_team.team_name,
        });
      }
      circles[circle].add(pair);
    }
  }

  const totalMatches = tours.reduce((sum, t) => sum + t.matches.length, 0);

  return {
    isValid: duplicates.length === 0,
    duplicates,
    totalMatches,
  };
}
